import { Skeleton, Table, TableProps } from "antd";
import React, { useState, useMemo, useEffect } from "react";
import { FaEye, FaTrashAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { OrgSuperAdmin } from "../../../store/types/settings";
import { getOrgAdmins } from "./misc/useAdmin";
import { VerificationStatus } from "./misc/VerifyStatus";
import SearchInput from "../../../components/elements/forms/SearchInput";
import { BiSearchAlt } from "react-icons/bi";
import DeleteConfirmModal from "../../../components/elements/DeleteConfirmModal";
import { deleteAdmin } from "../../../services/api/settings";
import { useAppDispatch } from "../../../store/hooks";

const RequestDemoAdmins: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { organizations, loading, pagination, refetch } = getOrgAdmins();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<OrgSuperAdmin | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // console.log("Organizations with demo admins:", organizations);

  // Fetch all data on mount to enable search across all pages
  useEffect(() => {
    refetch({ limit: 1000 }); // Fetch large limit to get all data
  }, []);

  // Filter organizations based on search term (client-side)
  const filteredOrganizations = useMemo(() => {
    if (!searchTerm || !organizations) return organizations;
    
    const lowerSearch = searchTerm.toLowerCase();
    return organizations.filter(org => 
      (org.representativeName?.toLowerCase().includes(lowerSearch)) ||
      (org.representativeEmail?.toLowerCase().includes(lowerSearch)) ||
      (org.representativeRole?.toLowerCase().includes(lowerSearch)) ||
      (org.name?.toLowerCase().includes(lowerSearch))
    );
  }, [organizations, searchTerm]);

  const columns: TableProps<OrgSuperAdmin>["columns"] = [
    {
      title: "S/N",
      dataIndex: "key",
      width: 60,
      align: "center",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Admin Name",
      dataIndex: "firstName",
      render: (_, record) => (
        <div className="font-medium">{record?.representativeName}</div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (_, record) => <div>{record?.representativeEmail}</div>,
    },
    {
      title: "Role",
      dataIndex: "role",
      render: (_, record) => (
        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
          {record?.representativeRole
            ?.replace(/_+/g, " ")
            .toLowerCase()
            ?.replace(/\b\w/g, (char) => char.toUpperCase())}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "isVerified",
      render: (isVerified: boolean, record: any) => (
        <VerificationStatus isVerified={isVerified} />
      ),
    },
    {
      title: "Plan",
      dataIndex: "subscriptionTier",
      render: (_, record) => (
        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
          {record?.subscriptionTier
            ?.replace(/_+/g, " ")
            .toLowerCase()
            ?.replace(/\b\w/g, (char) => char.toUpperCase())}
        </span>
      ),
    },
    // {
    //   title: "Last Login",
    //   dataIndex: "lastLoginAt",
    //   render: (date) => {
    //     if (!date) return <span className="text-gray-400">Never</span>;
    //     return new Date(date).toLocaleDateString();
    //   },
    // },
    {
      title: "Date Created",
      dataIndex: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div className="flex items-center justify-center gap-3">
          <Link
            to={`/admins/show/${record?.id}`}
            className="cursor-pointer text-blue-600 hover:text-blue-800"
          >
            <FaEye className="text-[18px] text-[green]" />
          </Link>
          <div
            onClick={() => handleDeleteClick(record)}
            className="cursor-pointer text-red-600 hover:text-red-800"
          >
            <FaTrashAlt className="text-[18px]" />
          </div>
        </div>
      ),
    },
  ];

  const handleTableChange = (pagination: any) => {
    refetch({
      page: pagination.current,
      limit: pagination.pageSize,
    });
  };

  const handleDeleteClick = (admin: OrgSuperAdmin) => {
    setSelectedAdmin(admin);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAdmin?.id) return;
    
    setDeleteLoading(true);
    const success = await deleteAdmin(selectedAdmin.id, dispatch);
    setDeleteLoading(false);
    
    if (success) {
      setDeleteModalOpen(false);
      setSelectedAdmin(null);
      // Refresh the table data
      refetch({
        page: pagination?.page || 1,
        limit: 10,
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedAdmin(null);
  };

  return (
    <div className="w-full py-5 font-abel">
      <div className="max-w-[1096px] mx-auto px-3">
        <div className="flex border-b border-border text-20 pt-3">
          <div
            onClick={() => navigate(`#`)}
            className="border-b-[3px] border-secondary pb-1 cursor-pointer"
          >
            <p className="font-[400]">Requested Demo Admins</p>
          </div>
        </div>
        <div className="rounded-10 bg-white px-4 py-5 mt-5">
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : organizations ? (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">
                    Admins ({pagination?.total || organizations.length})
                  </h3>
                  <p className="text-sm text-gray-600">
                    Manage administrators across all organizations
                  </p>
                </div>
                <SearchInput
                  className="w-full max-w-[280px]"
                  name="search"
                  value={searchTerm}
                  placeholder="Search by name, email..."
                  icon={<BiSearchAlt size={18} color="#243677" />}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Table
                dataSource={filteredOrganizations}
                columns={columns}
                loading={loading}
                rowKey="id"
                pagination={{
                  current: pagination?.page,
                  pageSize: 10,
                  total: pagination?.total,
                  showSizeChanger: false,
                  showTotal: (total) => `Total ${total} Requests`,
                }}
                onChange={handleTableChange}
                // scroll={{ x: 1000 }}
              />
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p>No admin data available.</p>
              <p className="text-sm">
                Please check your data source and try again.
              </p>
            </div>
          )}
        </div>
      </div>
      
      <DeleteConfirmModal
        candidate={{
          interviewId: selectedAdmin?.id || "",
          candidateId: selectedAdmin?.id || "",
          name: selectedAdmin?.name || selectedAdmin?.representativeName || "this organization",
        }}
        isOpen={deleteModalOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        loading={deleteLoading}
      />
    </div>
  );
};

export default RequestDemoAdmins;
